import type { MessageKey } from '@ralph/contracts'
import { formatCount, pluralCategory } from '@/i18n/format.ts'
import type { ServerMessages } from '@/i18n/messages/en/errors/index.ts'

/**
 * What the marketplace domain's refusals say, in English (the source of truth AND the type).
 *
 * This is the builder's half of the product speaking: almost every sentence here names the
 * one thing to go and change, because a maker who is told "precondition failed" has been told
 * nothing. Where two server throws carry the same sentence they carry the same KEY, so the
 * duplication stays in the API — where it is nine guard clauses — rather than arriving here
 * as nine translations of one idea.
 *
 * The locale is written out as a literal in the plural and number helpers below. That is not
 * a shortcut: this file IS English, the pt-BR sibling IS Brazilian Portuguese, and a catalogue
 * that asked which locale it was would be a catalogue that could be wrong about it.
 *
 * The `Extract` rather than a hand-written union: every `marketplace.*` key in the contract
 * belongs in this file, so adding one there and forgetting it here fails HERE, naming the key.
 */
export const marketplace: Pick<ServerMessages, Extract<MessageKey, `marketplace.${string}`>> = {
  'marketplace.report_not_found': () => 'That report no longer exists.',

  // --- identity and reach ----------------------------------------------------

  // Absent-or-hidden and suspended are `agents.*`: one sentence each for the whole product.
  'marketplace.agent_no_longer_usable': () =>
    'You can no longer use this agent, so new messages cannot be sent. This conversation stays readable.',
  'marketplace.agent_retired': () => 'This agent has been retired by its owner.',
  'marketplace.agent_retired_with_replacement': ({ replacement }) =>
    `This agent has been retired by its owner. “${replacement}” replaces it.`,
  'marketplace.agent_turned_off': () => 'This agent has been turned off by an administrator.',

  // --- the builder -----------------------------------------------------------

  'marketplace.agent_changed_while_editing': () =>
    'Somebody saved this agent while you were editing. Reload it and reapply your change.',
  'marketplace.agent_address_taken': () => 'An agent with that address already exists. Pick a different one.',
  'marketplace.agent_id_or_name_taken': () => 'An agent with that name or address already exists.',
  'marketplace.owner_must_be_you_or_a_managed_group': () =>
    'You can create an agent for yourself or for a group you manage.',
  'marketplace.attachment_not_available': () =>
    'One of those knowledge bases is not available to you. Reload the list and choose again.',
  'marketplace.preview_not_opened': () => 'The preview could not be opened. Try again.',
  /** Ungrouped: a version is an identifier the person clicked, not a quantity. */
  'marketplace.version_not_found': ({ version }) => `This agent has no version ${version} to go back to.`,
  /**
   * Two whole sentences rather than one with a spliced-in "time(s)". `pluralCategory` and not
   * `used === 1`, because Brazilian Portuguese puts zero in the `one` category — and a limit
   * an administrator has set to zero refuses on the very first preview (PRD-27 D-19).
   */
  'marketplace.preview_budget_spent': ({ used }) =>
    pluralCategory('en', used) === 'one'
      ? 'You have tried an agent in the builder once today, which is as many previews as one person can run in a day. Try again tomorrow, or ask an administrator to raise the limit.'
      : `You have tried agents in the builder ${formatCount('en', used)} times today, which is as many previews as one person can run in a day. Try again tomorrow, or ask an administrator to raise the limit.`,

  // --- who may act -----------------------------------------------------------

  'marketplace.requires_maintainer': () =>
    'Only the people who maintain this agent can do that. Ask them to make the change.',
  'marketplace.publish_requires_maintainer': () => 'Only the people who maintain this agent can publish it.',
  'marketplace.unpublish_requires_maintainer': () => 'Only the people who maintain this agent can unpublish it.',
  'marketplace.archive_requires_maintainer': () => 'Only the people who maintain this agent can retire it.',
  'marketplace.restore_requires_maintainer': () => 'Only the people who maintain this agent can bring it back.',
  'marketplace.move_requires_maintainer': () => 'Only the people who maintain this agent can move it.',
  'marketplace.audience_requires_maintainer': () =>
    'Only the people who maintain this agent can choose who can use it.',
  'marketplace.suspend_requires_admin': () => 'Only an administrator can suspend an agent.',
  'marketplace.unsuspend_requires_admin': () => 'Only an administrator can lift a suspension.',
  'marketplace.invitation_not_accepted': () => 'Accept the invitation to co-edit this agent before changing it.',

  // --- the lifecycle gate ----------------------------------------------------

  'marketplace.audience_needs_a_group': () => 'Pick at least one group to share this agent with.',
  'marketplace.publish_blocked_archived': () => 'This agent is retired. Bring it back before publishing again.',
  'marketplace.publish_blocked_suspended': () => 'This agent is suspended. An administrator has to lift that first.',
  'marketplace.publish_needs_a_name': () => 'Give this agent a name before publishing it.',
  'marketplace.publish_needs_a_saved_version': () => 'Save this agent before publishing it.',
  'marketplace.not_published': () => 'This agent is not published.',
  'marketplace.archive_needs_published': () => 'Only a published agent can be retired.',
  'marketplace.restore_needs_archived': () => 'Only a retired agent can be brought back.',
  'marketplace.restore_needs_a_saved_version': () => 'Save this agent before bringing it back.',
  'marketplace.already_suspended': () => 'This agent is already suspended.',
  'marketplace.suspension_needs_reasons': () =>
    'A suspension needs a reason for the owner and one for the people using it.',
  'marketplace.not_suspended': () => 'This agent is not suspended.',

  // --- names (AGM-005) -------------------------------------------------------

  'marketplace.name_reserved': () =>
    'That name is reserved. Pick a different one, or ask an administrator to publish it for you.',
  'marketplace.name_taken': () => 'Another agent already uses that name. Pick a different one.',
  'marketplace.name_recently_retired': () =>
    'That name belonged to an agent that was retired recently and is still held. Pick a different one, or ask an administrator about it.',
  'marketplace.name_just_taken': () => 'Another agent just took that name. Choose a different one and publish again.',

  // --- the content lint (AGM-024) --------------------------------------------

  'marketplace.lint_instructions_empty': () =>
    'This agent has no instructions yet. Describe what it should do and how it should answer — without them it cannot run.',
  'marketplace.lint_trust_mark_name': () =>
    'Names cannot include “official”, “verified” or a check-mark symbol. Only an administrator can mark an agent as official.',
  'marketplace.lint_trust_mark_tagline': () =>
    'Taglines cannot include “official”, “verified” or a check-mark symbol. Only an administrator can mark an agent as official.',
  'marketplace.lint_trust_mark_starter': () =>
    'Conversation starters cannot include “official”, “verified” or a check-mark symbol.',
  'marketplace.lint_trust_mark_text': () => 'This text cannot include “official”, “verified” or a check-mark symbol.',
  'marketplace.lint_instructions_short': () =>
    'These instructions are very short. A few sentences about what the agent does, who it is for and what it should refuse make its answers much more consistent.',
  /**
   * Three whole sentences with a repeated tail, rather than one template and a spliced-in
   * accusation. The API used to build these by dropping a gerund clause into `they are ___`,
   * which English permits and Portuguese does not (D-20) — and the repetition is the cheaper
   * half of that trade, because three sentences a translator can read whole are worth more
   * than one sentence plus a bag of parts.
   */
  'marketplace.lint_contradicts_citations': () =>
    'These instructions look like they are telling the agent not to cite its sources. The platform’s own rules always apply, so this line will not take effect and may confuse the agent.',
  'marketplace.lint_contradicts_platform_rules': () =>
    'These instructions look like they are telling the agent to ignore the platform rules. The platform’s own rules always apply, so this line will not take effect and may confuse the agent.',
  'marketplace.lint_contradicts_claims_human': () =>
    'These instructions look like they are telling the agent to claim it is a person. The platform’s own rules always apply, so this line will not take effect and may confuse the agent.',
  'marketplace.lint_impersonation_name': () =>
    'This name reads as if the agent speaks for a department. If it is not published by that department, choose a name that says who it is really from.',
  'marketplace.lint_impersonation_tagline': () =>
    'This tagline claims to speak for the company or a department. Describe what the agent helps with instead.',

  // --- health (AGM-048) ------------------------------------------------------
  //
  // Not refusals. These are the findings the platform computes about an agent and shows its
  // owner — in the health panel in the builder, and again in the insights panel, from the same
  // record. They live here because this is where a `MessageKey` sentence lives; what makes
  // them one family with the rest is the seam, not the mood.
  //
  // Each one names the actual thing that broke. The panel's heading and remedy come from the
  // maker catalogue and are keyed by the coarse `code`; this sentence is the specific middle
  // line between them, and it is the only one of the three that can say WHICH base or WHICH
  // capability.

  'marketplace.health_owner_group_unmanaged': () =>
    'Nobody manages the group that owns this agent any more, so nobody can change it. It keeps working; an administrator can appoint a manager or move it.',
  'marketplace.health_owner_deactivated': () =>
    'The person who owns this agent no longer has an account. It keeps working, but it needs a new owner — an administrator can move it.',
  'marketplace.health_agent_unreadable': () =>
    'This agent could not be read. Open it in the builder and save it again; if that does not help, an administrator can look at it.',
  'marketplace.health_agent_cannot_run': () =>
    'This agent cannot run as configured. Check that it still has instructions, and save it again.',
  'marketplace.health_knowledge_deleted': ({ base }) =>
    `The knowledge base “${base}” was deleted. This agent can no longer answer from it — attach a different one, or remove it from the list.`,
  'marketplace.health_knowledge_disabled': ({ base }) =>
    `The knowledge base “${base}” has been turned off, so this agent gets nothing from it.`,
  'marketplace.health_knowledge_unknown': () =>
    'One of the knowledge bases this agent uses no longer exists. Open the builder and check the list.',
  'marketplace.health_coverage_gap': ({ base }) =>
    `Some people who can use this agent cannot use “${base}”, so their answers leave it out. Its managers can let it answer through this agent for everyone it reaches — ask them from the builder.`,
  'marketplace.health_builtin_capability_gone': ({ capability }) =>
    `One of the things this agent was allowed to do (“${capability}”) no longer exists.`,
  'marketplace.health_capability_withdrawn': ({ capability }) =>
    `“${capability}” is no longer offered by the system it came from. This agent will not be able to use it.`,
  'marketplace.health_capability_quarantined': ({ capability }) =>
    `“${capability}” changed and is on hold until an administrator has reviewed it. This agent will not use it in the meantime.`,
  'marketplace.health_capability_switched_off': ({ capability }) =>
    `“${capability}” has been switched off by an administrator, so this agent cannot use it.`,

  // --- copying (AGM-030) -----------------------------------------------------

  'marketplace.copy_blocked': () => 'The owner of this agent has turned off copying.',
  'marketplace.copy_restricted_to_groups': () =>
    'This agent is shared with specific groups, so its owner decides who may copy it.',

  // --- moving (D-32) ---------------------------------------------------------

  // "It already belongs there" is `knowledge.already_owned_there`: one sentence, named after
  // neither of the two things it refuses a move for.
  'marketplace.move_target_not_managed': () =>
    'You can move it to a group you manage. An administrator can move it anywhere.',

  // --- co-editors (AGM-026) --------------------------------------------------

  'marketplace.editor_already_maintains': () => 'This person already maintains the agent.',
  'marketplace.editor_not_found': () => 'That person is not a co-editor of this agent.',
  'marketplace.no_open_invitation': () => 'There is no open invitation for you on this agent.',

  // --- engagement (AGM-121/123) ----------------------------------------------

  'marketplace.message_not_available': () => 'No message with that id is available to you.',
  'marketplace.feedback_needs_an_answer': () => 'Feedback belongs on an answer, not on your own message.',
  'marketplace.feedback_not_counted_in_preview': () => 'Answers in the builder preview are not counted as feedback.',
  'marketplace.report_already_open': () =>
    'You have already reported this agent and an administrator has not finished looking at it yet. There is no need to report it again.',
  'marketplace.report_not_filed': () => 'The report could not be filed. Try again.',

  // --- administration --------------------------------------------------------

  'marketplace.settings_must_be_whole': () => 'Send the whole settings document, not a partial one.',
  /** `detail` is the validator's own field paths and type names, English by design (D-17). */
  'marketplace.settings_invalid': ({ detail }) => `These settings are not valid: ${detail}`
}
